import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Receipt, CreditCard, AlertCircle, Check, Download, ExternalLink, Loader } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_BADGE = {
  completed: 'bg-green-500/10 text-green-400 border-green-500/20',
  pending:   'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  failed:    'bg-red-500/10 text-red-400 border-red-500/20',
  refunded:  'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

const ORDER_STATUS = {
  active:    'bg-green-500/10 text-green-400',
  suspended: 'bg-red-500/10 text-red-400',
  cancelled: 'bg-gray-500/10 text-gray-400',
  pending:   'bg-yellow-500/10 text-yellow-400',
};

const BillingPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('subscriptions');
  const [cancelling, setCancelling] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/billing/invoices'),
      api.get('/billing/orders'),
    ]).then(([inv, ord]) => {
      setInvoices(inv.data.invoices);
      setOrders(ord.data.orders);
    }).catch(() => toast.error('Failed to load billing data'))
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = async (orderId, serverName) => {
    if (!window.confirm(`Cancel subscription for "${serverName}"? Your server stays active until the end of this billing period.`)) return;
    setCancelling(orderId);
    try {
      await api.post(`/billing/cancel/${orderId}`);
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: 'cancelled' } : o));
      toast.success('Subscription cancelled. Your server stays active until the period ends.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to cancel subscription');
    } finally {
      setCancelling(null);
    }
  };

  const totalSpent = invoices
    .filter((i) => i.status === 'completed')
    .reduce((sum, i) => sum + parseFloat(i.amount), 0);

  return (
    <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-1">Billing</h1>
          <p className="text-gray-400">Manage your subscriptions and view payment history</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <CreditCard className="w-6 h-6 text-green-400 mb-3" />
            <div className="text-2xl font-black text-white">{orders.filter((o) => o.status === 'active').length}</div>
            <div className="text-gray-400 text-sm mt-1">Active subscriptions</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <Receipt className="w-6 h-6 text-blue-400 mb-3" />
            <div className="text-2xl font-black text-white">{invoices.length}</div>
            <div className="text-gray-400 text-sm mt-1">Total invoices</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
            <Check className="w-6 h-6 text-purple-400 mb-3" />
            <div className="text-2xl font-black text-white">€{totalSpent.toFixed(2)}</div>
            <div className="text-gray-400 text-sm mt-1">Total spent</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-gray-900 border border-gray-800 rounded-xl p-1 w-fit">
          {['subscriptions', 'invoices'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${
                tab === t ? 'bg-green-500 text-gray-950' : 'text-gray-400 hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader className="w-8 h-8 text-green-400 animate-spin" />
          </div>
        ) : tab === 'subscriptions' ? (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="text-center py-16 bg-gray-900 border border-gray-800 border-dashed rounded-2xl">
                <CreditCard className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 mb-4">No subscriptions yet</p>
                <Link to="/pricing" className="bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-5 py-2.5 rounded-xl text-sm transition-colors">
                  Browse Plans
                </Link>
              </div>
            ) : (
              orders.map((order) => (
                <div key={order.id} className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-white">{order.server_name || 'Server'}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${ORDER_STATUS[order.status]}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm">{order.plan_name} Plan · {order.billing_cycle} billing</p>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-bold">€{parseFloat(order.amount).toFixed(2)}</div>
                      <div className="text-gray-500 text-xs">/{order.billing_cycle === 'monthly' ? 'mo' : order.billing_cycle === 'annually' ? 'yr' : 'qtr'}</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                      <div className="text-white text-sm font-semibold">{order.ram_mb / 1024}GB</div>
                      <div className="text-gray-500 text-xs">RAM</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                      <div className="text-white text-sm font-semibold">{order.disk_mb / 1024}GB</div>
                      <div className="text-gray-500 text-xs">Storage</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-xl p-3 text-center">
                      <div className="text-white text-sm font-semibold">{order.player_slots}</div>
                      <div className="text-gray-500 text-xs">Slots</div>
                    </div>
                  </div>

                  {order.next_due_date && order.status === 'active' && (
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
                      <AlertCircle className="w-4 h-4 text-yellow-400" />
                      Next payment due: <span className="text-white font-medium">{new Date(order.next_due_date).toLocaleDateString()}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {order.server_id && (
                      <Link
                        to={`/console/${order.server_id}`}
                        className="flex items-center gap-1.5 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-sm rounded-xl transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" /> Console
                      </Link>
                    )}
                    {order.status === 'active' && (
                      <button
                        onClick={() => handleCancel(order.id, order.server_name)}
                        disabled={cancelling === order.id}
                        className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 text-sm rounded-xl transition-colors disabled:opacity-50"
                      >
                        {cancelling === order.id ? <Loader className="w-4 h-4 animate-spin" /> : 'Cancel'}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          /* Invoices tab */
          <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
            {invoices.length === 0 ? (
              <div className="text-center py-16">
                <Receipt className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No invoices yet</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-left border-b border-gray-800">
                    <th className="px-6 py-4 font-medium">Description</th>
                    <th className="px-6 py-4 font-medium">Method</th>
                    <th className="px-6 py-4 font-medium">Amount</th>
                    <th className="px-6 py-4 font-medium">Status</th>
                    <th className="px-6 py-4 font-medium">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="text-gray-300 hover:bg-gray-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-medium text-white">{inv.plan_name} Plan</div>
                        <div className="text-gray-500 text-xs">{inv.server_name} · {inv.billing_cycle}</div>
                      </td>
                      <td className="px-6 py-4 capitalize">{inv.provider}</td>
                      <td className="px-6 py-4 font-semibold text-white">€{parseFloat(inv.amount).toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-0.5 border rounded-full text-xs font-medium capitalize ${STATUS_BADGE[inv.status] || STATUS_BADGE.pending}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500">{new Date(inv.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
